import React, { ReactElement } from 'react';
import SwipeableSlider from '../swipeable-slider/swipeable-slider.component';
import Image from '../image/image.component';
import FlexContainer from '../flex-container/flex-container.component';
import CheckAccess from '../check-access/check-access.component';
import DraggableItem from '../draggable-item/draggable-item.component';
import FormFieldUpload from '../form-field-upload/form-field-upload.component';
import ImageControlsBlock from '../image-controls-block/image-controls-block.component';
import FormService from '../../services/form-service';
import useStylesWidgetImageSlider from './widget-image-slider.styles';
import { FILE_EXTENSION_LIST_IMAGE } from '../../constants/system/file-extensions';

const WidgetImageSlider: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const IMAGE_ITEM_WIDTH: number = 200;

  const {
    imageList,
    imageTextPrefix,
    dropDeps,
    imageNewItems,
    imageFiles,
    imageCreatePermissions,
    imageDeletePermissions,
    defaultPreviewUrl,
    inputNameRenderer,
    onDropImage,
    onDeleteImage,
    onFileDelete,
    onFilesChange,
  } = props;

  const { classes } = useStylesWidgetImageSlider();

  return (
    <SwipeableSlider
      isCentered
      isDraggable
      withNoOffset
      withLightControls
      withControlsVisible
      slideItemWidth={IMAGE_ITEM_WIDTH}
    >
      {
        ([
          ...(
            imageList && !!imageList.length ? (
              imageList.map((imageItem: SliderImageItem, imageIndex: number): ReactElement => (
                <DraggableItem
                  key={imageItem.id}
                  itemId={imageItem.id}
                  index={imageIndex}
                  onDropItem={onDropImage}
                >
                  <FlexContainer withSpaceAround className={classes.widgetImageSliderItemEdit}>
                    <CheckAccess permissions={imageDeletePermissions}>
                      <ImageControlsBlock
                        isFloating
                        withDelete
                        onDeleteClick={(): void => {
                          if (onDeleteImage) {
                            onDeleteImage(imageIndex);
                          }
                        }}
                      />
                    </CheckAccess>
                    <FlexContainer withSpaceAround className={classes.widgetImageSliderItemEditImage}>
                      <Image
                        isDefault
                        text={`${imageTextPrefix}${imageItem.id}`}
                        link={imageItem.link}
                      />
                    </FlexContainer>
                  </FlexContainer>
                </DraggableItem>
              ))
            ) : []
          ),
          ...(
            imageNewItems && !!imageNewItems.length ? (
              imageNewItems.map((imageItem: SliderImageItem): ReactElement => {
                const imageId: number = imageItem.id;
                const formKeyImageItem: string = FormService.getIndexedFormFieldKey(imageId);
                const imageFile: File = imageFiles[imageId];

                return (
                  <CheckAccess key={imageId} permissions={imageCreatePermissions}>
                    <FlexContainer withSpaceAround className={classes.widgetImageSliderNewItem}>
                      <FormFieldUpload
                        isImage
                        isInline
                        isMultiple
                        withMultipleSelection
                        isDroppable={!imageFile}
                        defaultPreviewUrl={defaultPreviewUrl}
                        name={inputNameRenderer(formKeyImageItem)}
                        uploadFile={imageFile}
                        uploadFiles={imageFiles}
                        previewImageClassName={classes.widgetImageSliderNewItemPreviewImage}
                        dropTargetBoxClassName={classes.widgetImageSliderNewItemDropTargetBox}
                        buttonLabel={`${imageTextPrefix}${imageId}`}
                        dropDeps={dropDeps}
                        acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                        onFilesChange={(files: File[]): void => {
                          if (onFilesChange) {
                            onFilesChange(files, imageId);
                          }
                        }}
                        onFileDelete={(event: React.MouseEvent<Element, MouseEvent>): void => {
                          if (onFileDelete) {
                            onFileDelete(event, imageId);
                          }
                        }}
                      />
                    </FlexContainer>
                  </CheckAccess>
                );
              })
            ) : []
          ),
        ])
      }
    </SwipeableSlider>
  );
});

export default WidgetImageSlider;
